import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { configAPI, customerAPI } from '../../services/api';
import Over90DaysScreen from '../customer/Over90DaysScreen';
import RoleShell from './RoleShell';
import { LayoutDashboard, MessageSquare, CreditCard, Tv, User, ClipboardList, Repeat, Bell, Gauge } from 'lucide-react';

const HOME = { to: '/', icon: LayoutDashboard, label: 'Home', end: true, alsoOn: ['/portal'] }; // `/` is the front door; /portal is the older address of the same page
const SUPPORT = { to: '/portal/complaints', icon: MessageSquare, label: 'Support' };
const PAYMENTS = { to: '/portal/payments', icon: CreditCard, label: 'Payments' };
const SPEED = { to: '/portal/speedtest', icon: Gauge, label: 'Speed Test' }; // broadband customers only
const PROFILE = { to: '/portal/profile', icon: User, label: 'Profile' };
const PLANS = { to: '/portal/plans', icon: Repeat, label: 'Plans' };
const REQUESTS = { to: '/portal/requests', icon: ClipboardList, label: 'Requests' };
const NOTIFICATIONS = { to: '/portal/notifications', icon: Bell, label: 'Notifications' };

// Top-bar title: the menu entry whose path the page is under, else the portal name.
const titleFor = (pathname, items) => {
  const hit = items.filter(i => i.to !== '/' && pathname.startsWith(i.to)).sort((a, b) => b.to.length - a.to.length)[0];
  return pathname === '/' || pathname === '/portal' ? 'Home' : hit ? hit.label : 'Customer Portal';
};

// The customer portal: same frame as every other role (RoleShell), with the customer's own menu.
export default function CustomerLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Two brands run under one portal — CATV and Broadband are sold under separate
  // company names/logos — so the header shows both side by side instead of picking one.
  const [brands, setBrands] = useState({ catv: null, broadband: null });
  // Speed Test only makes sense for broadband customers — mirrors the hasBB derivation
  // used across the portal (see CustomerDashboard.js).
  const [hasBB, setHasBB] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Set when EVERY account on this login expired more than 90 days ago: the portal then shows only
  // that message (an individual account of a multi-account login is handled by the dashboard).
  const [over90, setOver90] = useState(null);

  // The shared manifest carries the admin app's shortcuts; customers installing the app get their own.
  useEffect(() => {
    const m = document.querySelector('link[rel="manifest"]');
    if (m && m.getAttribute('href') === '/manifest.json') m.href = '/static/manifest-customer-v1.json';
  }, []);

  useEffect(() => {
    configAPI.get().then(({ data }) => {
      const d = data?.data;
      if (!d) return;
      setBrands({
        catv: d.catv?.logo ? { name: d.catv.name, logo: d.catv.logo } : null,
        broadband: d.broadband?.logo ? { name: d.broadband.name, logo: d.broadband.logo } : null,
      });
    }).catch(() => {});

    Promise.all([customerAPI.getMe(), customerAPI.getMyAccounts().catch(() => null)]).then(([{ data }, accRes]) => {
      const customer = data?.data;
      const accounts = accRes?.data?.data || [];
      if (accounts.length ? accounts.every(a => a.expiredOver90Days) : customer?.expiredOver90Days) {
        setOver90((accounts[0] || customer).expiredOver90Days);
      }
      const bbSource = customer?.customerType !== 'catv' ? customer : customer?.linkedAccount;
      setHasBB((bbSource?.customerType === 'broadband' || bbSource?.customerType === 'both') && !!bbSource?.broadband);
    }).catch(() => {});
  }, []);

  // Refetch whenever the customer navigates (e.g. back out of Notifications) so the
  // badge clears once items have been read.
  useEffect(() => {
    customerAPI.getUnreadNotificationCount().then(({ data }) => {
      setUnreadCount(data?.data?.count || 0);
    }).catch(() => {});
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/portal/login');
  };

  const logos = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, minWidth: 0 }}>
      {brands.catv && <img src={brands.catv.logo} alt={brands.catv.name} style={{ height: 56, maxWidth: 110, objectFit: 'contain' }} />}
      {brands.catv && brands.broadband && <span style={{ color: 'var(--sidebar-text, #94a3b8)', fontWeight: 300, fontSize: '1.4rem' }}>|</span>}
      {brands.broadband && <img src={brands.broadband.logo} alt={brands.broadband.name} style={{ height: 56, maxWidth: 110, objectFit: 'contain' }} />}
      {!brands.catv && !brands.broadband && <Tv size={30} color="var(--primary)" />}
    </div>
  );
  const topLogos = (
    <>
      {brands.catv && <img src={brands.catv.logo} alt={brands.catv.name} />}
      {brands.broadband && <img src={brands.broadband.logo} alt={brands.broadband.name} />}
      {!brands.catv && !brands.broadband && <Tv size={18} color="var(--primary)" />}
    </>
  );

  // Expired more than 90 days on every account: nothing but the message and Sign Out.
  if (over90) {
    return (
      <RoleShell title="Account" logo={logos} topLogo={topLogos} centerLogo contentMaxWidth={640} onLogout={handleLogout}>
        <Over90DaysScreen info={over90} />
      </RoleShell>
    );
  }

  const tabs = [HOME, SUPPORT, PAYMENTS, ...(hasBB ? [SPEED] : []), PROFILE];
  const items = [HOME, SUPPORT, PAYMENTS, ...(hasBB ? [SPEED] : []), PROFILE, PLANS, REQUESTS,
    { ...NOTIFICATIONS, badge: unreadCount > 0 ? unreadCount : null }];

  return (
    <RoleShell
      items={items}
      tabs={tabs}
      title={titleFor(location.pathname, items)}
      logo={logos}
      topLogo={topLogos}
      centerLogo
      contentMaxWidth={640}
      hideTitleOnPhone
      onLogout={handleLogout}
      bell={(
        <button onClick={() => navigate('/portal/notifications')} title="Notifications"
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, display: 'flex', position: 'relative' }}>
          <Bell size={18} />
          {unreadCount > 0 && (
            <span style={{ position: 'absolute', top: 3, right: 3, minWidth: 8, height: 8, borderRadius: '50%', background: '#dc2626', border: '1.5px solid var(--bg-card, #fff)' }} />
          )}
        </button>
      )}
    >
      <Outlet />
    </RoleShell>
  );
}
